/**
 * Every word the scan screen says about a verdict, in plain English for someone reading
 * at arm's length in a busy moment. Pure, so the wording is tested.
 */
import type { CalendarDate, Catalog, History, Light, Reason, Verdict, WriteOffReason } from "@hitl/rotations-domain";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2027-03-15" → "15 Mar 2027". No Date object, so no timezone can shift the day. */
export function formatDay(date: CalendarDate): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** How each write-off reason reads to her and to her accountant. */
export const WRITE_OFF_LABEL: Record<WriteOffReason, string> = {
  broken: "Broken",
  coating: "Coating deteriorated",
  flowers: "Flowers faded or damaged",
  lost: "Lost or stolen",
  other: "Other",
};

export const VERDICT_WORD: Record<Light, string> = {
  green: "Fine here",
  amber: "Check first",
  red: "Not here",
};

/** How each attribute reads in a sentence: "white flowers", "large pots". */
const ATTRIBUTE_NOUN: Record<string, string> = { colour: "flowers", size: "pots" };

export interface CopyContext {
  readonly potId: string;
  readonly clientName: string;
  readonly spotLabel: string;
  readonly catalog: Catalog;
}

function list(values: readonly string[]): string {
  return values.length <= 1 ? (values[0] ?? "") : `${values.slice(0, -1).join(", ")} or ${values.at(-1)}`;
}

export function reasonLine(reason: Reason, ctx: CopyContext): string {
  switch (reason.code) {
    case "unknown-pot":
      return `${ctx.potId} isn't in your pot list. Check the sticker, or place it anyway with a reason.`;
    case "pot-retired":
      return `${ctx.potId} is marked as retired.`;
    case "pot-written-off":
      return `${ctx.potId} was written off on ${formatDay(reason.on)} (${WRITE_OFF_LABEL[reason.reason].toLowerCase()}).`;
    case "repeat-blocked":
      return `${ctx.clientName} had ${ctx.potId} from ${formatDay(reason.lastPlacedOn)}. Allowed again from ${formatDay(reason.allowedFrom)}.`;
    case "history-unverified":
      return `Your records start ${formatDay(reason.historyKnownSince)}, so the full 12 months can't be checked.`;
    case "requirement-not-met": {
      const who = reason.level === "client" ? ctx.clientName : ctx.spotLabel;
      const has = reason.potValues.length ? list(reason.potValues) : "unknown";
      const noun = ATTRIBUTE_NOUN[reason.requirement.attribute];
      const what = noun ? `${list(reason.requirement.values)} ${noun}` : `${list(reason.requirement.values)} (${reason.requirement.attribute})`;
      const wanted = reason.requirement.rule === "only" ? `${who} wants ${what} only` : `${who} doesn't want ${what}`;
      return `${wanted}; this one is ${has}.`;
    }
    case "recorded-elsewhere": {
      const slot = ctx.catalog.slot(reason.slotId);
      const client = ctx.catalog.client(reason.clientId)?.name ?? reason.clientId;
      return `Records say it's at ${client}, ${slot.label}, since ${formatDay(reason.placedOn)}.`;
    }
  }
}

/** The evidence line under the reasons: the record the verdict rests on. */
export function citeLine(verdict: Verdict, potId: string, history: History, catalog: Catalog): string | null {
  // These reasons already cite the record they rest on; repeating it is noise.
  if (verdict.reasons.some((r) => r.code === "unknown-pot" || r.code === "repeat-blocked" || r.code === "recorded-elsewhere")) return null;
  const last = history.placementsOf(potId).at(-1);
  if (!last) return "First time out: no placements on record.";
  const client = catalog.client(last.clientId)?.name ?? last.clientId;
  return `Last placed: ${client}, ${formatDay(last.placedOn)}`;
}

/**
 * The 12-month window drawn to scale for a red: where today sits between the placement that
 * blocks it and the day it is allowed again. 0 = placed, 1 = allowed.
 */
export function windowProgress(lastPlacedOn: CalendarDate, allowedFrom: CalendarDate, today: CalendarDate): number {
  const span = utcDay(allowedFrom) - utcDay(lastPlacedOn);
  if (span <= 0) return 1;
  return Math.min(1, Math.max(0, (utcDay(today) - utcDay(lastPlacedOn)) / span));
}

function utcDay(date: CalendarDate): number {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}
